import React, { useState, useEffect, useRef } from 'react';
import { Plus, Upload, Trash2, FileText } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { type Word } from '../../types';
import { WordForm } from './WordForm';

export function WordBank() {
  const [words, setWords] = useState<Word[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingWord, setEditingWord] = useState<Partial<Word> | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);

  useEffect(() => {
    loadWords();
  }, []);

  const loadWords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('word_bank')
        .select('*')
        .order('dutch', { ascending: true });

      if (error) throw error;
      setWords(data || []);
    } catch (error) {
      console.error('Error loading words:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingWord) return;

    try {
      if (editingWord.id) {
        const { error } = await supabase
          .from('word_bank')
          .update({
            dutch: editingWord.dutch,
            translation: editingWord.translation,
            pronunciation: editingWord.pronunciation,
            example1: editingWord.example1,
            example1_translation: editingWord.example1_translation,
            example2: editingWord.example2,
            example2_translation: editingWord.example2_translation,
            example3: editingWord.example3,
            example3_translation: editingWord.example3_translation
          })
          .eq('id', editingWord.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('word_bank')
          .insert([{
            dutch: editingWord.dutch,
            translation: editingWord.translation,
            pronunciation: editingWord.pronunciation,
            example1: editingWord.example1,
            example1_translation: editingWord.example1_translation,
            example2: editingWord.example2,
            example2_translation: editingWord.example2_translation,
            example3: editingWord.example3,
            example3_translation: editingWord.example3_translation
          }]);

        if (error) throw error;
      }

      await loadWords();
      setEditingWord(null);
    } catch (error) {
      console.error('Error saving word:', error);
      alert('Error saving word. Please try again.');
    }
  };

  const handleDeleteWord = async (wordId: string) => {
    if (!window.confirm('Are you sure you want to delete this word?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('word_bank')
        .delete()
        .eq('id', wordId);

      if (error) throw error;
      await loadWords();
    } catch (error) {
      console.error('Error deleting word:', error);
      alert('Error deleting word. Please try again.');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setImporting(true);
      const text = await file.text();
      const rows = text.split('\n').map(row => {
        const cells = row.split(',');
        return cells.map(cell => cell.trim());
      });
      
      // Process each row with error handling for duplicates
      for (const row of rows) {
        if (row.length < 2) continue; // Skip if no word or translations
        
        const wordData = {
          dutch: row[0],
          translation: row[1],
          persian_translation: row[2] || null,
          pronunciation: row[3] || null,
          example1: row[4] || null,
          example1_translation: row[5] || null,
          example1_persian_translation: row[6] || null,
          example2: row[7] || null,
          example2_translation: row[8] || null,
          example2_persian_translation: row[9] || null,
          example3: row[10] || null,
          example3_translation: row[11] || null,
          example3_persian_translation: row[12] || null
        };

        const { error } = await supabase
          .from('word_bank')
          .upsert(wordData, {
            onConflict: 'dutch',
            ignoreDuplicates: false
          });

        if (error) {
          console.error('Error processing word:', row[0], error);
          continue;
        }
      }

      alert('Successfully imported words from CSV');
      await loadWords();

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error importing words:', error);
      alert('Error importing words. Please check your CSV format and try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) {
      alert('Please enter some text to import');
      return;
    }

    try {
      setBulkImporting(true);
      
      // Split the text into rows (each row is a word entry)
      const rows = bulkText.split('\n').filter(row => row.trim());
      
      let successCount = 0;
      let errorCount = 0;
      
      // Process each row
      for (const row of rows) {
        const parts = row.split('|').map(part => part.trim());
        
        if (parts.length < 2) {
          console.error('Invalid row format:', row);
          errorCount++;
          continue; // Skip invalid rows
        }
        
        const wordData = {
          dutch: parts[0],
          translation: parts[1],
          persian_translation: parts[2] || null,
          pronunciation: parts[3] || null,
          example1: parts[4] || null,
          example1_translation: parts[5] || null,
          example1_persian_translation: parts[6] || null,
          example2: parts[7] || null,
          example2_translation: parts[8] || null,
          example2_persian_translation: parts[9] || null,
          example3: parts[10] || null,
          example3_translation: parts[11] || null,
          example3_persian_translation: parts[12] || null
        };

        // Check if word is valid
        if (!wordData.dutch || !wordData.translation) {
          console.error('Word or translation missing:', wordData);
          errorCount++;
          continue;
        }

        try {
          const { error } = await supabase
            .from('word_bank')
            .upsert(wordData, {
              onConflict: 'dutch',
              ignoreDuplicates: false
            });

          if (error) {
            console.error('Error importing word:', wordData.dutch, error);
            errorCount++;
          } else {
            successCount++;
          }
        } catch (error) {
          console.error('Error processing word:', wordData.dutch, error);
          errorCount++;
        }
      }

      alert(`Import completed: ${successCount} words imported successfully, ${errorCount} errors.`);
      await loadWords();
      
      // Reset form
      setBulkText('');
      setShowBulkImport(false);
    } catch (error) {
      console.error('Error importing words:', error);
      alert('Error importing words. Please check the format and try again.');
    } finally {
      setBulkImporting(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedWords.length === words.length) {
      setSelectedWords([]);
    } else {
      setSelectedWords(words.map(word => word.id!));
    }
  };

  const handleSelectWord = (wordId: string) => {
    setSelectedWords(prev => 
      prev.includes(wordId)
        ? prev.filter(id => id !== wordId)
        : [...prev, wordId]
    );
  };

  const handleDeleteSelected = async () => {
    if (!selectedWords.length) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedWords.length} selected words?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('word_bank')
        .delete()
        .in('id', selectedWords);

      if (error) throw error;
      
      setSelectedWords([]);
      await loadWords();
    } catch (error) {
      console.error('Error deleting selected words:', error);
      alert('Error deleting words. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Word Bank</h2>
          <p className="mt-1 text-sm text-gray-500 max-w-xl">
            CSV format: word, english_translation, persian_translation, pronunciation, example1, english_translation1, persian_translation1, example2, english_translation2, persian_translation2, example3, english_translation3, persian_translation3
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              ref={fileInputRef}
              disabled={importing}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${
                importing
                  ? 'bg-gray-400 cursor-not-allowed opacity-50'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              <Upload className="h-4 w-4 mr-2" />
              {importing ? 'Importing...' : 'Import CSV'}
            </button>
          </div>
          <div className="h-6 w-px bg-gray-300"></div>
          <button
            onClick={handleSelectAll}
            className="text-gray-600 hover:text-gray-800 px-3 py-1 rounded hover:bg-gray-100"
          >
            {selectedWords.length === words.length ? 'Deselect All' : 'Select All'}
          </button>
          {selectedWords.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center px-3 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected ({selectedWords.length})
            </button>
          )}
          <button
            onClick={() => setShowBulkImport(true)}
            disabled={bulkImporting}
            className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${
              bulkImporting
                ? 'bg-gray-400 cursor-not-allowed opacity-50'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            <FileText className="h-4 w-4 mr-2" />
            {bulkImporting ? 'Processing...' : 'Bulk Import'}
          </button>
          <div className="h-6 w-px bg-gray-300"></div>
        </div>
        <button
          onClick={() => setEditingWord({
            dutch: '',
            translation: '',
            pronunciation: '',
            example: '',
            example_translation: ''
          })}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Word
        </button>
      </div>

      {showBulkImport && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Bulk Import Words</h3>
              <button
                onClick={() => setShowBulkImport(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter words in the following format:
                  <br />
                  <span className="text-gray-500">
                    word | english_translation | persian_translation | pronunciation | example1 | example1_translation | example1_persian_translation | example2 | example2_translation | example2_persian_translation | example3 | example3_translation | example3_persian_translation
                  </span>
                </label>
                <textarea
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={10}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="aanbieden | to offer | پیشنهاد دادن | aan-bie-den | Ik wil je een kopje koffie aanbieden | I want to offer you a cup of coffee | می‌خواهم به شما یک فنجان قهوه پیشنهاد کنم"
                />
              </div>
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  One word entry per line. Each field separated by | character.
                </div>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowBulkImport(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkImport}
                    disabled={bulkImporting}
                    className={`px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white ${
                      bulkImporting ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {bulkImporting ? 'Importing...' : 'Import Words'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingWord && (
        <WordForm
          word={editingWord}
          onSave={handleSaveWord}
          onChange={setEditingWord}
          onClose={() => setEditingWord(null)}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {words.map((word) => (
          <div
            key={word.id}
            className="bg-white p-4 rounded-lg shadow-md space-y-2 hover:shadow-lg transition-shadow duration-200"
          >
            <div className="flex items-start gap-4">
              <input
                type="checkbox"
                checked={selectedWords.includes(word.id!)}
                onChange={() => handleSelectWord(word.id!)}
                className="mt-1.5 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
              />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{word.dutch}</h3>
                <div className="space-y-1">
                  <p className="text-gray-600">English: {word.translation}</p>
                  {word.persian_translation && (
                    <p className="text-gray-600">Persian: {word.persian_translation}</p>
                  )}
                </div>
                {word.pronunciation && (
                  <p className="text-sm text-gray-500">/{word.pronunciation}/</p>
                )}
              </div>
              <div className="flex space-x-2 ml-2">
                <button
                  onClick={() => setEditingWord(word)}
                  className="text-indigo-600 hover:text-indigo-800 p-1 hover:bg-indigo-50 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteWord(word.id!)}
                  className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                >
                  Delete
                </button>
              </div>
            </div>
            {(word.example1 || word.example1_translation) && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-gray-800">{word.example1}</p>
                <p className="text-gray-600 italic">English: {word.example1_translation}</p>
                {word.example1_persian_translation && (
                  <p className="text-gray-600 italic">Persian: {word.example1_persian_translation}</p>
                )}
                {(word.example2 || word.example2_translation) && (
                  <>
                    <p className="text-gray-800 mt-2">{word.example2}</p>
                    <p className="text-gray-600 italic">English: {word.example2_translation}</p>
                    {word.example2_persian_translation && (
                      <p className="text-gray-600 italic">Persian: {word.example2_persian_translation}</p>
                    )}
                  </>
                )}
                {(word.example3 || word.example3_translation) && (
                  <>
                    <p className="text-gray-800 mt-2">{word.example3}</p>
                    <p className="text-gray-600 italic">English: {word.example3_translation}</p>
                    {word.example3_persian_translation && (
                      <p className="text-gray-600 italic">Persian: {word.example3_persian_translation}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )).sort((a, b) => {
          const aSelected = selectedWords.includes(a.key as string);
          const bSelected = selectedWords.includes(b.key as string);
          return aSelected === bSelected ? 0 : aSelected ? -1 : 1;
        })}
      </div>

      {words.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No words found</p>
          <button
            onClick={() => setEditingWord({
              dutch: '',
              translation: '',
              pronunciation: '',
              example: '',
              example_translation: ''
            })}
            className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Word
          </button>
        </div>
      )}
    </div>
  );
}